// src/cockpit4/components/EnterpriseBrainPanel.tsx
// ============================================
// 企业大脑（Obsidian Vault）热插拔面板
//
// 商业化标准方案：
//   「更换企业 = 更换大脑（Vault），系统零改动」
//   1. 服务器端：切换 Vault 根路径（/vault-api/switch-vault，持久化到 vault-config.json）
//   2. 前端：配置 Obsidian 连接（URL/API Key）+ 选择大脑 Runtime
//   3. 每个企业只需：部署一套 XEAOS + 挂载该企业 Vault → 立即成为该企业的 AI 操作系统
// ============================================

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Brain,
  FolderTree,
  Server,
  Link2,
  KeyRound,
  RefreshCw,
  Loader2,
  Save,
  CheckCircle2,
  RotateCcw,
  Shield,
  Rocket,
} from "lucide-react";
import { vaultHotswap } from "../../runtime/shared/vaultHotswap";

interface VaultConfigResp {
  success: boolean;
  vaultRoot?: string;
  vaultConfigFile?: string;
}

const BRAIN_RUNTIMES = [
  { id: "enterprise_brain", label: "Enterprise Brain（企业总脑）", desc: "统一目标/战略/决策/KPI/风险编排，每日 08:30 自动运行" },
  { id: "business_operator", label: "Business Operator（企业运营官）", desc: "每日 21:30 自动检查 8 项运营指标并主动补救" },
  { id: "hermes", label: "Hermes（行政助手）", desc: "AI 行政助手，对话/知识/任务统一入口" },
];

export default function EnterpriseBrainPanel() {
  const [serverVaultRoot, setServerVaultRoot] = useState("");
  const [configFile, setConfigFile] = useState("");
  const [newVaultRoot, setNewVaultRoot] = useState("");
  const [obsidianUrl, setObsidianUrl] = useState("");
  const [obsidianApiKey, setObsidianApiKey] = useState("");
  const [brainRuntime, setBrainRuntime] = useState("enterprise_brain");
  const [enterpriseName, setEnterpriseName] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  // 初始化：读取服务器 Vault 配置 + 本地热插拔配置
  const load = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const resp = await fetch("/vault-api/config", { headers: { Accept: "application/json" } });
      if (resp.ok) {
        const json: VaultConfigResp = await resp.json();
        if (json?.vaultRoot) setServerVaultRoot(json.vaultRoot);
        if (json?.vaultConfigFile) setConfigFile(json.vaultConfigFile);
      }
    } catch { /* 服务器不可用时忽略 */ }
    try {
      const hs = vaultHotswap.get();
      setObsidianUrl(hs.obsidianUrl);
      setObsidianApiKey(hs.obsidianApiKey);
      setBrainRuntime(hs.brainRuntime || "enterprise_brain");
      setEnterpriseName(hs.enterpriseName);
    } catch { /* 忽略 */ }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  // 保存本地热插拔配置（Obsidian 连接 + 大脑 Runtime + 企业名）
  const handleSaveLocal = () => {
    vaultHotswap.update({
      obsidianUrl: obsidianUrl.trim(),
      obsidianApiKey: obsidianApiKey.trim(),
      brainRuntime,
      enterpriseName: enterpriseName.trim(),
    });
    setMessage({ type: "ok", text: "✅ 本地企业大脑配置已保存（Obsidian 连接 + 大脑 Runtime），刷新后生效" });
  };

  // 切换服务器 Vault 根路径
  const handleSwitchServerVault = async () => {
    const root = newVaultRoot.trim();
    if (!root) {
      setMessage({ type: "err", text: "请输入新的 Vault 根路径（如 D:/Obsidian/某企业中央大脑库）" });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const resp = await fetch("/vault-api/switch-vault", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vaultRoot: root }),
      });
      const json = await resp.json();
      if (json?.success) {
        setServerVaultRoot(json.savedVaultRoot || root);
        setMessage({ type: "ok", text: `✅ ${json.message || "Vault 根路径已保存"}` });
      } else {
        setMessage({ type: "err", text: `⚠️ ${json?.error || "切换失败"}` });
      }
    } catch (err: any) {
      setMessage({ type: "err", text: `⚠️ ${err?.message || "请求失败"}` });
    } finally {
      setSaving(false);
    }
  };

  const handleResetLocal = () => {
    vaultHotswap.reset();
    setObsidianUrl("");
    setObsidianApiKey("");
    setBrainRuntime("enterprise_brain");
    setEnterpriseName("");
    setMessage({ type: "ok", text: "✅ 已重置为默认配置" });
  };

  return (
    <div className="space-y-3">
      {/* 标准化方案说明 */}
      <div className="rounded-md border border-emerald-500/25 bg-emerald-500/[0.04] px-3 py-2.5">
        <div className="flex items-center gap-2 mb-1">
          <Rocket size={12} className="text-emerald-400" />
          <span className="text-[11px] font-mono text-emerald-300">商业化标准方案</span>
        </div>
        <p className="text-[10px] font-mono text-gray-400 leading-relaxed">
          「更换企业 = 更换大脑（Vault），系统零改动」。每个企业只需：部署一套 XEAOS + 挂载该企业 Vault
          → 立即成为该企业的 AI 操作系统。定制仅发生在 Vault 内容与企业数据，代码层改动极小。
        </p>
      </div>
      {/* 服务器 Vault 当前状态 */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.01] px-3 py-2.5">
        <div className="flex items-center gap-2 mb-2">
          <Server size={12} className="text-cyan-400" />
          <span className="text-[11px] font-mono text-gray-300">服务器 Vault 根路径</span>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="ml-auto p-1 text-gray-500 hover:text-cyan-400 transition-colors"
            title="刷新服务器配置"
          >
            <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-[10px] font-mono text-gray-500">
            <Loader2 size={10} className="animate-spin" /> 读取中...
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[11px] font-mono text-gray-300 break-all">
              <FolderTree size={11} className="text-cyan-500 flex-shrink-0" />
              {serverVaultRoot || "未连接服务器"}
            </div>
            {configFile && (
              <div className="text-[9px] font-mono text-gray-600 mt-1 truncate">配置持久化：{configFile}</div>
            )}
          </>
        )}
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            value={newVaultRoot}
            onChange={(e) => setNewVaultRoot(e.target.value)}
            placeholder="输入新企业 Vault 根路径（服务器端）"
            className="flex-1 px-2.5 py-1.5 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-cyan-500/40 placeholder-gray-600"
          />
          <button
            onClick={handleSwitchServerVault}
            disabled={saving || loading}
            className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded transition-colors disabled:opacity-40 flex-shrink-0"
          >
            {saving ? <Loader2 size={10} className="animate-spin" /> : <Link2 size={10} />}
            切换企业大脑
          </button>
        </div>
        <p className="text-[9px] font-mono text-gray-600 mt-1.5">
          切换后需在服务器执行 pm2 restart xeaos 生效。每个企业独立 Vault，即完成换脑。
        </p>
      </div>
      {/* 本地连接配置 */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.01] px-3 py-2.5">
        <div className="flex items-center gap-2 mb-2">
          <Brain size={12} className="text-purple-400" />
          <span className="text-[11px] font-mono text-gray-300">企业名称 & Obsidian 连接（本地）</span>
        </div>
        <div className="space-y-2">
          <input
            type="text"
            value={enterpriseName}
            onChange={(e) => setEnterpriseName(e.target.value)}
            placeholder="企业名称（如：小舍科技 / 某酒店集团）"
            className="w-full px-2.5 py-1.5 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-purple-500/40 placeholder-gray-600"
          />
          <input
            type="text"
            value={obsidianUrl}
            onChange={(e) => setObsidianUrl(e.target.value)}
            placeholder="Obsidian Local REST API URL（如 http://127.0.0.1:27123/）"
            className="w-full px-2.5 py-1.5 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-purple-500/40 placeholder-gray-600"
          />
          <input
            type="password"
            value={obsidianApiKey}
            onChange={(e) => setObsidianApiKey(e.target.value)}
            placeholder="Obsidian API Key（企业自有 Obsidian 时配置）"
            className="w-full px-2.5 py-1.5 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-purple-500/40 placeholder-gray-600"
          />
          <div className="flex items-center gap-2">
            <KeyRound size={11} className="text-gray-500 flex-shrink-0" />
            <button
              onClick={handleSaveLocal}
              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded transition-colors"
            >
              <Save size={10} /> 保存本地配置
            </button>
            <button
              onClick={handleResetLocal}
              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-mono text-gray-500 hover:text-gray-300 border border-white/[0.06] rounded transition-colors"
            >
              <RotateCcw size={10} /> 重置
            </button>
          </div>
        </div>
      </div>
      {/* 大脑 Runtime 选择 */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.01] px-3 py-2.5">
        <div className="flex items-center gap-2 mb-2">
          <Shield size={12} className="text-amber-400" />
          <span className="text-[11px] font-mono text-gray-300">大脑 Runtime（企业总脑）</span>
        </div>
        <div className="space-y-1.5">
          {BRAIN_RUNTIMES.map((b) => (
            <button
              key={b.id}
              onClick={() => setBrainRuntime(b.id)}
              className={`w-full text-left px-2.5 py-2 rounded-md border transition-colors ${
                brainRuntime === b.id
                  ? "border-amber-500/40 bg-amber-500/[0.06]"
                  : "border-white/[0.05] bg-white/[0.01] hover:border-white/[0.1]"
              }`}
            >
              <div className="flex items-center gap-2">
                {brainRuntime === b.id && <CheckCircle2 size={11} className="text-amber-400" />}
                <span className="text-[11px] font-mono text-gray-200">{b.label}</span>
              </div>
              <p className="text-[9px] font-mono text-gray-600 mt-0.5 pl-5">{b.desc}</p>
            </button>
          ))}
        </div>
        <p className="text-[9px] font-mono text-gray-600 mt-2">
          当前：{BRAIN_RUNTIMES.find((b) => b.id === brainRuntime)?.label || brainRuntime}
        </p>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`px-3 py-2 rounded-md border text-[10px] font-mono ${
            message.type === "ok"
              ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
              : "border-amber-500/30 bg-amber-500/[0.06] text-amber-300"
          }`}
        >
          {message.text}
        </motion.div>
      )}
    </div>
  );
}
